/**
 * NightSlidesCard: the placement control on the Game Night hub.
 *
 * Lists the custom slides placed into this night, in play order, and lets
 * the host place a library slide at a position: at the start, before or
 * after a round, at halftime, or at the end. A preview shows the chosen
 * slide before it goes in.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Flex, Select, Text } from '@radix-ui/themes';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import {
  ANCHOR_OPTIONS,
  customSlideLabel,
  fetchSlideTemplates,
  placementLabel,
  type SlideTemplate,
} from '../../utils/customSlides';
import type { CustomSlide, SlideAnchor } from '../../utils/slideSequence';
import SlidePreview, { templateAsSlide } from './SlidePreview';

interface Props {
  liveEventId: string;
  venueName: string;
  rounds: { orderIndex: number; title: string }[];
  customSlides: CustomSlide[];
  onChange: () => Promise<void> | void;
}

export default function NightSlidesCard({ liveEventId, venueName, rounds, customSlides, onChange }: Props) {
  const [templates, setTemplates] = useState<SlideTemplate[] | null>(null);
  const [templateId, setTemplateId] = useState('');
  const [anchor, setAnchor] = useState<SlideAnchor>('START');
  const [roundNumber, setRoundNumber] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchSlideTemplates().then(setTemplates).catch(() => setTemplates([]));
  }, []);

  const needsRound = ANCHOR_OPTIONS.find((o) => o.value === anchor)?.needsRound ?? false;
  const selected = templates?.find((t) => t.id === templateId);
  const canAdd = Boolean(templateId) && (!needsRound || Boolean(roundNumber)) && !busy;

  const add = async () => {
    if (!canAdd) return;
    setBusy(true);
    try {
      await api.post('/api/slides/placements', {
        liveEventId,
        templateId,
        anchor,
        roundNumber: needsRound ? Number(roundNumber) : null,
        // New slides go after the ones already at this position.
        sortOrder: customSlides.length,
      });
      setTemplateId('');
      await onChange();
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } };
      toast.error(e.response?.data?.message || 'Failed to place slide');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (placementId: string) => {
    setBusy(true);
    try {
      await api.delete(`/api/slides/placements/${placementId}`);
      await onChange();
    } catch {
      toast.error('Failed to remove slide');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gh-card">
      <span className="gh-overline">SLIDES · {customSlides.length} PLACED</span>
      {customSlides.length === 0 ? (
        <span className="gh-card-body">
          No custom slides tonight. Add a sponsor, a reminder or next week&rsquo;s schedule.
        </span>
      ) : (
        <Flex direction="column" gap="1">
          {customSlides.map((c) => (
            <div className="gh-override-row" key={c.id}>
              <span className="gh-override-label">{customSlideLabel(c)}</span>
              <span className="gh-override-value">
                <span className={c.fellBack ? 'gh-check-detail warn' : 'gh-override-source'}>
                  {placementLabel(c)}
                </span>{' '}
                <Button
                  size="1"
                  variant="ghost"
                  color="red"
                  disabled={busy}
                  onClick={() => remove(c.id)}
                  aria-label={`Remove ${customSlideLabel(c)}`}
                >
                  Remove
                </Button>
              </span>
            </div>
          ))}
        </Flex>
      )}

      {templates && templates.length === 0 ? (
        <span className="gh-card-body">
          Your slide library is empty. <Link to="/slides">Make a slide</Link> first.
        </span>
      ) : (
        <Flex direction="column" gap="2" mt="2">
          <Select.Root value={templateId} onValueChange={setTemplateId}>
            <Select.Trigger placeholder="Pick a slide…" aria-label="Slide to place" />
            <Select.Content>
              {(templates ?? []).map((t) => (
                <Select.Item key={t.id} value={t.id}>{t.name}</Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
          <Flex gap="2" wrap="wrap">
            <Select.Root value={anchor} onValueChange={(v) => setAnchor(v as SlideAnchor)}>
              <Select.Trigger aria-label="Position" />
              <Select.Content>
                {ANCHOR_OPTIONS.map((o) => (
                  <Select.Item key={o.value} value={o.value}>{o.label}</Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
            {needsRound && (
              <Select.Root value={roundNumber} onValueChange={setRoundNumber}>
                <Select.Trigger placeholder="Round…" aria-label="Round" />
                <Select.Content>
                  {rounds.map((r) => (
                    <Select.Item key={r.orderIndex} value={String(r.orderIndex + 1)}>
                      Round {r.orderIndex + 1}{r.title ? ` · ${r.title}` : ''}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            )}
          </Flex>
          {selected && <SlidePreview slide={templateAsSlide(selected)} venueName={venueName} />}
          <Flex justify="between" align="center" gap="2">
            <Text size="1" color="gray">
              <Link to="/slides">Manage slide library</Link>
            </Text>
            <Button size="1" onClick={add} disabled={!canAdd}>
              {busy ? 'Placing…' : 'Place slide'}
            </Button>
          </Flex>
        </Flex>
      )}
    </div>
  );
}
